import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Paperclip } from "lucide-react";
import AttachmentsPanel from "./AttachmentsPanel";

const SWATCHES = [
  "#0F1115", "#0090FF", "#46A758", "#FFC53D", "#F76B15", "#E54D2E",
  "#8E4EC6", "#0E7AB2", "#707070", "#E93D82", "#BB9457", "#1B9AAA",
];

export default function CategoryManager({ open, onOpenChange, kind, onChanged }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openAttachmentsFor, setOpenAttachmentsFor] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/categories/${kind}`);
    setItems(data);
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line
  }, [open, kind]);

  const setRow = (id, patch) =>
    setItems((arr) => arr.map((r) => (r.id === id ? { ...r, ...patch, _dirty: true } : r)));

  const saveRow = async (row) => {
    const payload = {
      name: row.name,
      color: row.color,
      order: row.order,
      hidden: !!row.hidden,
    };
    setBusy(true);
    try {
      if (row._new) {
        await api.post(`/categories/${kind}`, payload);
      } else if (row._dirty) {
        await api.put(`/categories/${kind}/${row.id}`, payload);
      }
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveAll = async () => {
    setBusy(true);
    try {
      for (const row of items) {
        if (row._new) {
          await api.post(`/categories/${kind}`, {
            name: row.name,
            color: row.color,
            order: row.order,
            hidden: !!row.hidden,
          });
        } else if (row._dirty) {
          await api.put(`/categories/${kind}/${row.id}`, {
            name: row.name,
            color: row.color,
            order: row.order,
            hidden: !!row.hidden,
          });
        }
      }
      toast.success("Categories saved");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (row._new) {
      setItems((arr) => arr.filter((r) => r.id !== row.id));
      return;
    }
    if (!window.confirm(`Delete category "${row.name}"? Existing entries in this category will not be deleted.`)) return;
    await api.delete(`/categories/${kind}/${row.id}`);
    toast.success("Category deleted");
    await load();
    onChanged && onChanged();
  };

  const move = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= items.length) return;
    const copy = [...items];
    [copy[idx], copy[ni]] = [copy[ni], copy[idx]];
    copy.forEach((r, i) => {
      r.order = i;
      r._dirty = true;
    });
    setItems(copy);
  };

  const addNew = () => {
    setItems((arr) => [
      ...arr,
      {
        id: `new-${Date.now()}`,
        _new: true,
        name: "New Category",
        color: SWATCHES[arr.length % SWATCHES.length],
        order: arr.length,
        hidden: false,
      },
    ]);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-white w-[480px] sm:max-w-none" data-testid="category-manager">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">Manage Categories</SheetTitle>
        </SheetHeader>
        <div className="mt-4 text-xs text-neutral-500">
          Rename, recolor, reorder, hide or remove. Renaming a category does not move its existing entries.
        </div>
        <div className="mt-4 space-y-2 max-h-[70vh] overflow-y-auto pr-1">
          {items.map((row, idx) => (
            <div key={row.id} className="border border-[var(--rw-border)] rounded-[4px]" data-testid={`cat-row-${row.id}`}>
              <div className="flex items-center gap-2 p-2">
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => move(idx, -1)} className="text-neutral-400 hover:text-neutral-700" data-testid={`cat-up-${idx}`}>
                    <ArrowUp size={12} />
                  </button>
                  <button onClick={() => move(idx, 1)} className="text-neutral-400 hover:text-neutral-700" data-testid={`cat-down-${idx}`}>
                    <ArrowDown size={12} />
                  </button>
                </div>
                <div className="relative">
                  <input
                    type="color"
                    value={row.color || "#0F1115"}
                    onChange={(e) => setRow(row.id, { color: e.target.value })}
                    className="w-7 h-7 rounded-sm cursor-pointer border border-[var(--rw-border)]"
                    data-testid={`cat-color-${idx}`}
                  />
                </div>
                <Input
                  value={row.name}
                  onChange={(e) => setRow(row.id, { name: e.target.value })}
                  className="flex-1"
                  data-testid={`cat-name-${idx}`}
                />
                <button
                  onClick={() => setOpenAttachmentsFor(openAttachmentsFor === row.id ? null : row.id)}
                  className={openAttachmentsFor === row.id ? "text-[var(--rw-sidebar)]" : "text-neutral-400 hover:text-neutral-700"}
                  title="Documents for this category"
                  data-testid={`cat-attach-${idx}`}
                >
                  <Paperclip size={14} />
                </button>
                <button
                  onClick={() => setRow(row.id, { hidden: !row.hidden })}
                  className="text-neutral-400 hover:text-neutral-700"
                  title={row.hidden ? "Show" : "Hide"}
                  data-testid={`cat-hide-${idx}`}
                >
                  {row.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button
                  onClick={() => remove(row)}
                  className="text-red-400 hover:text-red-600"
                  data-testid={`cat-delete-${idx}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              {openAttachmentsFor === row.id && !row._dirty && (
                <div className="border-t border-[var(--rw-border)] p-2">
                  <AttachmentsPanel
                    targetKind="category"
                    targetId={row.id}
                    title={`${row.name} documents`}
                  />
                </div>
              )}
              {openAttachmentsFor === row.id && row._dirty && (
                <div className="border-t border-[var(--rw-border)] p-2 text-xs text-neutral-500 italic">
                  Save changes first, then attach documents.
                </div>
              )}
            </div>
          ))}
          {items.length === 0 && (
            <div className="text-sm text-neutral-400 text-center py-6">
              No categories yet. Defaults will be created automatically when you open an Operations tab.
            </div>
          )}
        </div>
        <div className="mt-4 flex gap-2">
          <Button variant="outline" onClick={addNew} data-testid="cat-add">
            <Plus size={14} className="mr-1" /> Add Category
          </Button>
          <Button
            onClick={saveAll}
            disabled={busy}
            className="bg-[var(--rw-sidebar)] hover:bg-[var(--rw-sidebar-hover)] ml-auto"
            data-testid="cat-save-all"
          >
            Save Changes
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
